import { useTranslation } from 'react-i18next';

import { designFormattedList, getPermsGroup } from '@/features/cards/components/bulkCardCreation/helpers';
import { cardsMediums, IBulkCard, isValidCardMedium } from '@/features/cards/store/models/bulk-card.model';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

interface IResumeBulkCardCreationProps {
  bulkCardCreation: IBulkCard;
}

export const ResumeBulkCardCreation = ({ bulkCardCreation }: IResumeBulkCardCreationProps) => {
  const { t } = useTranslation();

  const config = useDashboardConfig();

  const permsgroup = getPermsGroup(bulkCardCreation?.defaultPermsGroup);
  const bulkCardCreationData = [
    {
      [t('pages.administration.bulkCards.stepper.labels.quantity')]:
        bulkCardCreation && bulkCardCreation?.number
          ? bulkCardCreation?.number
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
    },
    {
      [t('pages.administration.bulkCards.stepper.labels.type')]: isValidCardMedium(bulkCardCreation?.medium)
        ? t(
            `pages.administration.bulkCards.creationDetails.${cardsMediums[Object.keys(cardsMediums).find((key) => cardsMediums[key].value === bulkCardCreation?.medium)].label}`,
          )
        : t(`Unknown Medium`),
    },
    {
      [t('pages.administration.bulkCards.stepper.summary.cardName')]:
        bulkCardCreation && bulkCardCreation?.anonymous === 0
          ? t('pages.administration.bulkCards.creationDetails.anonymedCard')
          : t('pages.administration.bulkCards.creationDetails.anonymous'),
    },
    {
      [t('pages.administration.bulkCards.stepper.summary.permsGroupActivated')]:
        bulkCardCreation && bulkCardCreation?.defaultPermsGroup
          ? `${permsgroup.nfc ? 'NFC,' : ''}${permsgroup.atm ? ' Withdrawal,' : ''}${
              permsgroup.foreign ? ' Foreign,' : ''
            }${permsgroup.online ? ' Online,' : ''}`
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
    },
    {
      [t('pages.administration.bulkCards.stepper.summary.walletId')]:
        bulkCardCreation && bulkCardCreation?.walletIdAttach
          ? bulkCardCreation?.walletIdAttach
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.userId')]:
        bulkCardCreation && bulkCardCreation?.userIdOwner
          ? bulkCardCreation?.userIdOwner
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
    },
    {
      [t('pages.administration.bulkCards.stepper.summary.gpsId')]:
        bulkCardCreation && bulkCardCreation?.cardPrint
          ? bulkCardCreation?.cardPrint
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
    },
    {
      [t('pages.administration.bulkCards.stepper.summary.logoIdReference')]:
        bulkCardCreation && bulkCardCreation?.logoId
          ? bulkCardCreation?.logoId
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.packageIdReference')]:
        bulkCardCreation && bulkCardCreation?.packageId
          ? bulkCardCreation?.packageId
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      ...(config?.settings?.bulkCards?.batchDelivery && {
        [t('pages.administration.bulkCards.bulkoptions.batchDeliveryId')]:
          bulkCardCreation && bulkCardCreation?.batchDeliveryId
            ? bulkCardCreation?.batchDeliveryId
            : t('pages.administration.bulkCards.creationDetails.notDefined'),
      }),
      [t('pages.administration.bulkCards.stepper.summary.designCodeReference')]:
        bulkCardCreation && bulkCardCreation?.designCode
          ? bulkCardCreation?.designCode
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.restrictionMccGroup')]:
        bulkCardCreation && bulkCardCreation?.defaultMccRestrictionGroupId
          ? bulkCardCreation?.defaultMccRestrictionGroupId
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.restrictionMidGroup')]:
        bulkCardCreation && bulkCardCreation?.defaultMerchantRestrictionGroupId
          ? bulkCardCreation?.defaultMerchantRestrictionGroupId
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.restrictionCountriesGroup')]:
        bulkCardCreation && bulkCardCreation?.defaultCountryRestrictionGroupId
          ? bulkCardCreation?.defaultCountryRestrictionGroupId
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitAtmAll')]:
        bulkCardCreation && bulkCardCreation?.limitAtmAll
          ? bulkCardCreation?.limitAtmAll
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitAtmByDay')]:
        bulkCardCreation && bulkCardCreation?.limitAtmDay
          ? bulkCardCreation?.limitAtmDay
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitAtmByWeek')]:
        bulkCardCreation && bulkCardCreation?.limitAtmWeek
          ? bulkCardCreation?.limitAtmWeek
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitAtmByMonth')]:
        bulkCardCreation && bulkCardCreation?.limitAtmMonth
          ? bulkCardCreation?.limitAtmMonth
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitAtmByYear')]:
        bulkCardCreation && bulkCardCreation?.limitAtmYear
          ? bulkCardCreation?.limitAtmYear
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitPaymentAll')]:
        bulkCardCreation && bulkCardCreation?.limitPaymentAll
          ? bulkCardCreation?.limitPaymentAll
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitPaymentByDay')]:
        bulkCardCreation && bulkCardCreation?.limitPaymentDay
          ? bulkCardCreation?.limitPaymentDay
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitPaymentByWeek')]:
        bulkCardCreation && bulkCardCreation?.limitPaymentWeek
          ? bulkCardCreation?.limitPaymentWeek
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitPaymentByMonth')]:
        bulkCardCreation && bulkCardCreation?.limitPaymentMonth
          ? bulkCardCreation?.limitPaymentMonth
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
      [t('pages.administration.bulkCards.stepper.summary.limitPaymentByYear')]:
        bulkCardCreation && bulkCardCreation?.limitPaymentYear
          ? bulkCardCreation?.limitPaymentYear
          : t('pages.administration.bulkCards.creationDetails.notDefined'),
    },
  ];
  return (
    <div className="flex flex-col overflow-auto py-0 px-5 min-w-[22.5rem] max-h-[46vh]">
      {designFormattedList(bulkCardCreationData)}
    </div>
  );
};
